// src/pages/Steps/ChatbotCreationSteps.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Stepper, Step, StepLabel, Box, Paper, Typography, Button, CircularProgress } from "@mui/material";
import { styled } from '@mui/material/styles';
import { useNavigate} from 'react-router-dom';

// --- Import services and context ---
import api from '../../utils/api';
import { useChatbots } from '../../contexts/ChatbotContext';

// Import step components
import CreateChatbotName from '../ChatbotSettings/CreateChatbotName';
import AvatarSettings from '../AvatarSettings';
import ListAudioLanguage from '../AudioLanguage/ListAudioLanguage';
import Prompt from '../ChatbotSettings/Prompt';
import ChatbotTrainingInfo from '../ChatbotSettings/ChatbotTrainingInfo';

// Static Definition of ALL Possible Steps
const allSteps = [
    { label: 'Chatbot Name', slug: 'chatbot-name' },
    { label: 'Set Avatar', slug: 'set-avatar' },
    { label: 'Audio Language', slug: 'set-audio-language' },
    { label: 'Chatbot Settings', slug: 'set-chatbot-setting' },
    { label: 'Training Data Info', slug: 'chatbot-training-info' },
];

// Helper function to determine visible steps based on plan
const determineVisibleSteps = (subscription) => {
    const isFreemiumPlan = subscription?.plan?.name === 'freemium';
    if (isFreemiumPlan) {
        return allSteps.filter(step => step.slug !== 'set-audio-language');
    }
    return allSteps;
};

// Helper Components
const InlineError = ({ message }) => message ? <Typography color="error" sx={{ mt: 1, mb: 1, textAlign: 'center' }}>{message}</Typography> : null;
const InlineSpinner = () => <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />;
const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3), display: 'flex', flexDirection: 'column', gap: theme.spacing(2),
    margin: '50px auto', maxWidth: '70%', minWidth: '500px',
}));

export default function ChatbotCreationSteps({ onSetupComplete }) {
    const navigate = useNavigate();
    const { refreshChatbots } = useChatbots();

    // State Management
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState(false);
    const [error, setError] = useState(null);
    const [activeStep, setActiveStep] = useState(0);
    const [steps, setSteps] = useState([]);
    const [pendingChatbot, setPendingChatbot] = useState(null);

    // Refs for calling child methods
    const chatbotNameRef = useRef(null);
    const avatarSettingsRef = useRef(null);
    const promptRef = useRef(null);
    const audioLanguageRef = useRef(null);

    // Initialize and check for existing pending chatbot
    useEffect(() => {
        const initialize = async () => {
            setIsLoading(true);
            setError(null);
            
            try {
                const userStr = localStorage.getItem('user');
                const userData = userStr ? JSON.parse(userStr) : {};
                
                // Determine visible steps based on subscription
                const visibleSteps = determineVisibleSteps(userData.subscription);
                setSteps(visibleSteps);
                
                const response = await api.get(`/chatbots/user/pending-prcess-bot`);
                const botData = response.data.data;
                
                if (botData.chatbot) {
                    setPendingChatbot(botData.chatbot);
                    // Determine current step based on chatbot's current_process_step
                    const currentStepIndex = visibleSteps.findIndex(step => step.slug === botData.chatbot.current_process_step);
                    setActiveStep(currentStepIndex >= 0 ? currentStepIndex : 0); 
                }else{
                    // No pending chatbot, start fresh
                    setPendingChatbot(null);
                    setActiveStep(0);
                }

            } catch (err) {
                console.error('Error initializing chatbot setup:', err);
                setError("Could not load chatbot configuration.");
            } finally {
                setIsLoading(false);
            }
        };
        
        initialize();
    }, [navigate]);

    useEffect(() => {
        document.title = 'Chatbot Setup | KlonIT';
    }, []);


    // Handle step completion
    const handleStepCompletion = useCallback(async (data = {}) => {
        setIsProcessing(false);
        setError(null);
        
        try {
            
            const response = await api.get(`/chatbots/user/pending-prcess-bot`);
            const botData = response.data.data;
            
            if (botData.chatbot) {
                setPendingChatbot(botData.chatbot);
                // Find next step
                const nextStepIndex = steps.findIndex(step => step.slug === botData.chatbot.current_process_step);
                if (nextStepIndex !== -1) {
                    setActiveStep(nextStepIndex);
                } else {
                    // If current_process_step doesn't match any step, move to next step
                    setActiveStep(prevStep => Math.min(prevStep + 1, steps.length - 1));
                }
            }else{
                // No pending chatbot, start fresh
                setPendingChatbot(null);
                setActiveStep(0);
            }
        } catch (err) {
            console.error('Error in step completion:', err);
            setError("Could not proceed to the next step.");
        }
    }, [steps, navigate, refreshChatbots]);

    const handleStepError = useCallback((errorMessage) => {
        setIsProcessing(false);
        setError(errorMessage || "An error occurred in this step.");
    }, []);

    const handleNext = async () => {
        setError(null);
        setIsProcessing(true);
        
        if (activeStep < 0 || activeStep >= steps.length) {
            handleStepError("Invalid step sequence.");
            return;
        }
        
        const currentStepSlug = steps[activeStep].slug;
        
        try {
            let stepResult = false;
            
            // Handle different step types
            switch (currentStepSlug) {
                case 'chatbot-name':
                    if (chatbotNameRef.current?.triggerSave) {
                        stepResult = await chatbotNameRef.current.triggerSave();
                    }
                    break;
                    
                case 'set-avatar':
                    if (avatarSettingsRef.current?.triggerSave) {
                        stepResult = await avatarSettingsRef.current.triggerSave();
                    }
                    break;
                    
                case 'set-audio-language':
                    // For audio language, we just need to check if at least one language is configured
                    if (audioLanguageRef.current?.checkStepCompletion) {
                        stepResult = audioLanguageRef.current.checkStepCompletion();
                    } else {
                        // If no ref method, assume it's valid and handle server-side
                        stepResult = true;
                    }
                    break;
                    
                case 'set-chatbot-setting':
                    if (promptRef.current?.triggerSave) {
                        stepResult = await promptRef.current.triggerSave();
                    }
                    break;
                    
                case 'chatbot-training-info':
                    // This is an info step, just proceed
                    stepResult = true;
                    break;
                    
                default:
                    stepResult = true;
                    break;
            }
            
            if (stepResult) {
                // If we have a pending chatbot, update its progress

                if (pendingChatbot) {
                    console.log(pendingChatbot)
                    const isFinalStep = activeStep >= steps.length - 1;
                    const nextStepSlug = isFinalStep ? 'completed' : steps[activeStep + 1].slug;
                    
                    await api.put(`/chatbots/${pendingChatbot.id}`, {
                        current_process_step: nextStepSlug,
                        is_process_completed: isFinalStep,
                    });
                    
                    if (isFinalStep) {
                        refreshChatbots();
                        navigate(`/chatbot/${pendingChatbot.chatbot_identity}/dashboard`);
                        return;
                    }
                }
                
                // Proceed to handle step completion
                await handleStepCompletion();
            }
        } catch (e) {
            console.error('Error in handleNext:', e);
            handleStepError(e.message || "Failed to process step.");
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    };

    // Get step content based on current step
    const getStepContent = (stepIndex) => {
        if (isLoading) {
            return (
                <Box sx={{textAlign: 'center', p: 3}}>
                    <CircularProgress />
                </Box>
            );
        }
        
        console.log(pendingChatbot)
        const currentSlug = steps[stepIndex]?.slug;
        
        switch (currentSlug) {
            case 'chatbot-name':
                return (
                    <CreateChatbotName 
                        ref={chatbotNameRef} 
                        onComplete={handleStepCompletion} 
                        onError={handleStepError} 
                    />
                );
                
            case 'set-avatar':
                return (
                    <AvatarSettings 
                        ref={avatarSettingsRef}  
                        chatbotId={pendingChatbot ? pendingChatbot.chatbot_identity : null}
                        isSetupMode={true} 
                        onComplete={handleStepCompletion} 
                        onError={handleStepError} 
                    />
                );
                
            case 'set-audio-language':
                return (
                    <ListAudioLanguage  
                        ref={audioLanguageRef}
                        isSetupMode={true} 
                        chatbotId={pendingChatbot ? pendingChatbot.chatbot_identity : null}
                        onSetupComplete={handleStepCompletion}
                        onSetupError={handleStepError}
                    />
                );
                
            case 'set-chatbot-setting':
                return (
                    <Prompt 
                        ref={promptRef}  
                        isSetupMode={true} 
                        chatbotId={pendingChatbot ? pendingChatbot.chatbot_identity : null}
                        onComplete={handleStepCompletion} 
                        onError={handleStepError} 
                    />
                );
                
            case 'chatbot-training-info':
                return <ChatbotTrainingInfo />;
                
            default:
                return <Typography>Loading Step Content...</Typography>;
        }
    };
    
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress /> 
                <Typography sx={{ ml: 2 }}>Loading Setup...</Typography>
            </Box>
        );
    }
    
    if (error) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column' }}>
                <Typography color="error" variant="h6">Setup Error</Typography>
                <Typography color="error">{error}</Typography>
                <Button onClick={() => navigate('/my-chatbots')} sx={{mt: 2}}>
                    Return to My Chatbots
                </Button>
            </Box>
        );
    }
    
    return (
        <Box sx={{ width: '100%' }}>
            <StyledPaper elevation={3}>
                <Typography variant="h5" gutterBottom textAlign="center">
                   Set up Your Chatbot
                </Typography>
                
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                    {steps.map((step) => (
                        <Step key={step.slug}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                <InlineError message={error} />

                <Box mt={1} sx={{ minHeight: '300px' }}>
                    {getStepContent(activeStep)}
                </Box>

                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    pt: 2, 
                    borderTop: 1, 
                    borderColor: 'divider', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mt: 2 
                }}>
                    <Button
                        color="inherit"
                        disabled={activeStep === 0 || isProcessing || activeStep === 1 && pendingChatbot}
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <>
                                <InlineSpinner />
                                Processing...
                            </>
                        ) : (
                            activeStep === steps.length - 1 ? "Finish & Go to Dashboard" : "Save & Continue"
                        )}
                    </Button>
                </Box>
            </StyledPaper>
        </Box>
    );
}